import { useCallback, useLayoutEffect, useRef, type FocusEvent } from 'react';

type TextFieldElement = HTMLInputElement | HTMLTextAreaElement;

export function useFocusTextFieldAtEnd(dependencies: unknown[] = []) {
  const inputElementRef = useRef<TextFieldElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const cancelFocus = useCallback(() => {
    if (animationFrameRef.current != null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const focusInput = useCallback(
    (input = inputElementRef.current) => {
      if (!input) {
        return;
      }

      cancelFocus();
      animationFrameRef.current = window.requestAnimationFrame(() => {
        animationFrameRef.current = null;

        if (document.contains(input)) {
          placeCursorAtEnd(input);
        }
      });
    },
    [cancelFocus]
  );

  const inputRef = useCallback(
    (input: TextFieldElement | null) => {
      inputElementRef.current = input;

      if (input) {
        focusInput(input);
      }
    },
    [focusInput]
  );

  useLayoutEffect(() => {
    focusInput();

    return cancelFocus;
    // dependencies intentionally let callers reposition the caret when a new card/dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return {
    inputRef,
    onFocus: (event: FocusEvent<TextFieldElement>) => placeCursorAtEnd(event.currentTarget),
  };
}

function placeCursorAtEnd(input: TextFieldElement) {
  const cursorPosition = input.value.length;

  input.focus();

  if (typeof input.setSelectionRange === 'function') {
    input.setSelectionRange(cursorPosition, cursorPosition);
  }
}
